import { useEffect, useRef } from "react";
import { connect, useDispatch } from "react-redux";
import {
  getCurrentCompanyAusIqMiningProjectsAsync,
} from "features/company/companySlice";
import { Table } from "~/Common";
import { Box, Button, Grid, Heading, Text } from "theme-ui";
import { Icon } from "assets/Icon";

const mapStateToProps = (state) => ({
  company: state.companies?.currentCompany,
  companies: state.companies,
});

const MiningProjects = ({ company, companies }) => {
  const dispatch = useDispatch();
  const prevAsxRef = useRef();

  const { miningProjects, miningProjectsStatus } = companies?.currentCompanyExtras?.ausIq ?? {};

  const { projects } = miningProjects || {};

  useEffect(() => {
    const asx = company?.asx;
    if (!asx) return;

    const asxChanged = prevAsxRef.current !== asx;

    if (miningProjectsStatus === "idle" || asxChanged) {
      dispatch(getCurrentCompanyAusIqMiningProjectsAsync(asx));
    }

    prevAsxRef.current = asx;
  }, [company?.asx, dispatch, miningProjectsStatus]);

  const columns = [
    {
      key: "project",
      title: "Project",
      align: "left",
      render: (p) => (
        <Box sx={{ position: "relative", alignItems: "center" }}>
          <Heading as="h3" variant="h5" sx={{ mb: 1 }}>
            {p?.project_name || "-"}
          </Heading>
        </Box>
      ),
    },
    {
      key: "types",
      title: "Types",
      align: "left",
      render: (p) =>
        Array.isArray(p?.types) && p.types.length ? (
          <Text as="p" variant="h6" sx={{ textTransform: "capitalize" }}>
            {p.types.join(", ")}
          </Text>
        ) : (
          <Text as="p" variant="h6">-</Text>
        ),
    },
    {
      key: "commodities",
      title: "Processed Commodities",
      align: "left",
      render: (p) =>
        Array.isArray(p?.processed_commodities) &&
        p.processed_commodities.length ? (
          <Text as="p" variant="h6" sx={{ textTransform: "capitalize" }}>
            {p.processed_commodities.join(", ")}
          </Text>
        ) : (
          <Text as="p" variant="h6">-</Text>
        ),
    },
    {
      key: "stage",
      title: "Stage",
      align: "left",
      render: (p) => (
        <Text as="p" variant="h6" color="darker">
          {p?.stage || "-"}
        </Text>
      ),
    },
    {
      key: "location",
      title: "Location",
      align: "left",
      render: (p) => (
        <Text as="p" variant="h6">{p?.location || "-"}</Text>
      ),
    },
    {
      key: "report_date",
      title: "Report Date",
      align: "right",
      render: (p) => (
        <Text as="p" variant="h6" color="darker">
          {p?.report_date ? new Date(p.report_date).toLocaleDateString() : "-"}
        </Text>
      ),
    },
    {
      key: "sources",
      title: "Sources",
      align: "center",
      render: (p) =>
        Array.isArray(p?.url) && p.url.length ? (
          <Grid columns={[2, "auto auto"]} gap={2} sx={{ alignItems: "center", justifyContent: "flex-end" }}>
            {p.url.slice(0, 2).map((u, i) => (
              <Button
                key={`${p.project_id}-src-${i}`}
                as="a"
                href={u}
                target="_blank"
                rel="noreferrer"
                variant="icon"
                aria-label={`Open source ${i + 1}`}
              >
                <Icon icon="external-link" />
              </Button>
            ))}
          </Grid>
        ) : null,
    },
  ];

  const mapRowKey = (p) => `${p.project_id || "unknown"}-${p.report_date || "na"}`;

  const rowProps = () => ({
    sx: {
      textDecoration: "none",
      color: "darker",
      ".table-row__arrow": {
        transform: "translateX(-4px)",
        transition: "transform 0.3s ease-in-out",
      },
      "&:hover .table-row__arrow": {
        transform: "translateX(0)",
        color: "tealDark",
      },
    },
  });

  return (
    <Table
      data={projects}
      columns={columns}
      prefix="mining_projects"
      isLoading={miningProjectsStatus === "loading"}
      mapRowKey={mapRowKey}
      rowProps={rowProps}
      my={3}
      minWidth="1200px"
      emptyTemplate={{
        icon: "portfolio",
        heading: "No Mining Projects",
        content: (
          <Text variant="h6">
            We couldn’t find any projects for this company.
          </Text>
        ),
      }}
    />
  );
};

export default connect(mapStateToProps)(MiningProjects);
